import { NavItem, NavSection } from '@components/v2/layout/AppTemplate/appTemplate.types';
import {
  Archive,
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  FileText,
  Goal,
  Home,
  Network,
  NotepadText,
  Receipt,
  Settings,
  ShoppingBag,
  Store,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import LangEnum from '../../../lang/LangEnum';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { useFeatureVisibility } from '../../../modules/permissions/contexts/FeatureVisibilityContext';
import { F } from '../../../modules/permissions/types/features.keys';
import RachelSparkNavIcon from '../../../modules/rachel/components/RachelSparkIcon/RachelSparkNavIcon';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import PATHS from '../../routes.const';
import { useSubNavItems } from './useSubNavItems';

export const useHomeNavItems = () => {
  const { isHidden, isVisible } = useFeatureVisibility();
  const { translate } = useTranslation();
  const { state } = useApplicationContext();
  const { pathname } = useLocation();
  const isRachelAvailable = isVisible(F.menu.rachel);

  const showroomSlug = state?.user?.brand?.slug || state?.brand?.slug;

  const navItems: NavSection[] = useMemo(
    () => [
      {
        items: (
          [
            {
              label: translate(LangEnum.MAIN_DRAWER_HOME),
              icon: Home,
              pathname: PATHS.main.home.path,
              hidden: isHidden(F.menu.home),
            },
            {
              label: translate(LangEnum.MAIN_DRAWER_RACHEL),
              icon: RachelSparkNavIcon,
              pathname: PATHS.main.rachel.path,
              hidden: !isRachelAvailable,
            },
            {
              label: translate(LangEnum.MAIN_DRAWER_CONNECTIONS),
              icon: Network,
              pathname: PATHS.main.myNetwork.path,
              hidden: isHidden(F.menu.connections),
            },
            {
              label: translate(LangEnum.MAIN_DRAWER_SHOWROOM),
              icon: Store,
              pathname: showroomSlug ? `/${showroomSlug}` : PATHS.main.showroomStorefront.path,
              hidden: isHidden(F.menu.showroom),
            },
            {
              label: translate(LangEnum.MAIN_DRAWER_CATALOGS),
              icon: BookOpen,
              pathname: PATHS.main.catalogs.path,
              isActive: (p: string) =>
                p.startsWith(PATHS.main.catalogs.path) || p.startsWith(PATHS.main.products.path),
              hidden: isHidden(F.menu.catalogs),
            },
            {
              label: translate(LangEnum.MAIN_DRAWER_ORDERS),
              icon: NotepadText,
              pathname: PATHS.main.orders.path,
              hidden: isHidden(F.menu.orders),
            },
            {
              label: translate(LangEnum.STORE_ORDERS_MENU),
              icon: ShoppingBag,
              pathname: PATHS.main.storeOrders.path,
              hidden: isHidden(F.menu.storeOrders),
            },
            {
              label: translate(LangEnum.MAIN_DRAWER_CUSTOMERS),
              icon: Users,
              pathname: PATHS.main.customers.path,
              hidden: isHidden(F.menu.customers),
            },
            {
              label: translate(LangEnum.SALES_OPPORTUNITIES),
              icon: TrendingUp,
              pathname: PATHS.main.salesOpportunities.path,
              hidden: isHidden(F.menu.salesOpportunities),
            },
            {
              label: translate(LangEnum.GLOBAL_PLANS),
              icon: Goal,
              pathname: PATHS.main.targets.list.path,
              hidden: isHidden(F.menu.targets),
              isNew: true,
            },
            {
              label: translate(LangEnum.GLOBAL_REPORTS),
              icon: FileText,
              pathname: PATHS.main.reports.path,
              hidden: isHidden(F.menu.reports),
            },
            {
              label: translate(LangEnum.GLOBAL_ANALYTICS),
              icon: BarChart3,
              pathname: PATHS.main.analytics.path,
              hidden: isHidden(F.menu.analytics),
              isNew: true,
            },
            {
              label: translate(LangEnum.GLOBAL_CASES),
              icon: Archive,
              pathname: PATHS.main.cases.path,
              hidden: isHidden(F.menu.cases),
            },
            {
              label: translate(LangEnum.INVOICES_TITLE),
              icon: Receipt,
              pathname: PATHS.main.invoices.path,
              hidden: isHidden(F.menu.invoices),
            },
            {
              label: translate(LangEnum.ACCOUNTS_RECEIVABLE_TITLE),
              icon: ArrowLeftRight,
              pathname: PATHS.main.accountsReceivable.receivable.path,
              hidden: isHidden(F.menu.accountsReceivable.receivable),
            },
            {
              label: translate(LangEnum.ACCOUNTS_RECEIVABLE_PAYABLE_TITLE),
              icon: ArrowLeftRight,
              pathname: PATHS.main.accountsReceivable.payable.path,
              hidden: isHidden(F.menu.accountsReceivable.payable),
            },
            {
              label: translate(LangEnum.MAIN_DRAWER_SETTINGS),
              icon: Settings,
              pathname: PATHS.main.settings.path,
              hidden: isHidden(F.menu.settings),
            },
          ] as NavItem[]
        ).filter((item) => !item.hidden),
      },
    ],
    [isHidden, translate, showroomSlug, isRachelAvailable],
  );

  const subContextNavItems = useSubNavItems(pathname);

  return { navItems, subContextNavItems };
};
